import { axiosInstance } from '@/shared/lib/axios';
import type {
  SubscriptionPlan,
  ShopSubscription,
  SubscriptionPlanCreateInput,
  SubscriptionPlanUpdateInput,
  ShopSubscriptionCreateInput,
  ShopSubscriptionUpdateInput,
  Promotion,
  FounderSpotsData,
} from '@/shared/types';

const BASE_URL = '/api/v1';

export const subscription = {
  // Promotions
  getActivePromotion: async (): Promise<Promotion | null> => {
    try {
      const response = await axiosInstance.get(`${BASE_URL}/subscriptions/promotions/active`);
      return response.data;
    } catch {
      return null;
    }
  },

  // Founder Spots
  getFounderSpots: async (): Promise<FounderSpotsData | null> => {
    try {
      const response = await axiosInstance.get(`${BASE_URL}/subscriptions/founder-spots`);
      return response.data;
    } catch {
      return null;
    }
  },

  // Subscription Plans
  getSubscriptionPlans: async (
    active?: boolean,
  ): Promise<SubscriptionPlan[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/subscriptions/plans`, {
      params: { active },
    });

    return response.data;
  },

  getSubscriptionPlan: async (id: string): Promise<SubscriptionPlan> => {
    const response = await axiosInstance.get(
      `${BASE_URL}/subscriptions/plans/${id}`,
    );

    return response.data;
  },

  createSubscriptionPlan: async (
    plan: SubscriptionPlanCreateInput,
  ): Promise<SubscriptionPlan> => {
    const response = await axiosInstance.post(
      `${BASE_URL}/subscriptions/plans/admin`,
      plan,
    );
    return response.data;
  },

  updateSubscriptionPlan: async (
    id: string,
    updates: SubscriptionPlanUpdateInput,
  ): Promise<SubscriptionPlan> => {
    const response = await axiosInstance.put(
      `${BASE_URL}/subscriptions/plans/admin/${id}`,
      updates,
    );
    return response.data;
  },

  toggleSubscriptionPlan: async (id: string): Promise<SubscriptionPlan> => {
    const response = await axiosInstance.post(
      `${BASE_URL}/subscriptions/plans/admin/${id}/toggle`,
    );
    return response.data;
  },

  // Shop Subscriptions
  createShopSubscription: async (
    shopId: string,
    subscription: ShopSubscriptionCreateInput,
  ): Promise<ShopSubscription> => {
    const response = await axiosInstance.post(
      `${BASE_URL}/subscriptions/shop/${shopId}`,
      subscription,
    );
    return response.data;
  },

  getShopSubscription: async (shopId: string): Promise<ShopSubscription> => {
    const response = await axiosInstance.get(
      `${BASE_URL}/subscriptions/shop/${shopId}`,
    );
    return response.data;
  },

  updateShopSubscription: async (
    shopId: string,
    updates: ShopSubscriptionUpdateInput,
  ): Promise<ShopSubscription> => {
    const response = await axiosInstance.put(
      `${BASE_URL}/subscriptions/shop/${shopId}`,
      updates,
    );
    return response.data;
  },

  getProviderSubscriptions: async (
    providerId: string,
    isActive?: boolean,
    token?: string,
  ): Promise<ShopSubscription[]> => {
    const config = token
      ? {
          params: { isActive },
          headers: { Authorization: `Bearer ${token}` },
        }
      : { params: { isActive } };
    const response = await axiosInstance.get(
      `${BASE_URL}/subscriptions/provider/${providerId}`,
      config,
    );

    return response.data;
  },

  createCheckoutSession: async (
    planId: string,
    promotionTierId?: string,
    providerType?: string,
  ): Promise<{ sessionId: string; sessionUrl: string }> => {
    const response = await axiosInstance.post(
      `${BASE_URL}/subscriptions/create-checkout-session`,
      {
        planId,
        ...(promotionTierId && { promotionTierId }),
        ...(providerType && { providerType }),
      },
    );
    return response.data;
  },

  verifyCheckoutSession: async (
    sessionId: string,
    token?: string,
  ): Promise<{
    success: boolean;
    sessionId: string;
    providerId: string;
    providerRole: 'INDEPENDENT_PROVIDER' | 'SHOP_OWNER' | 'STAFF_PROVIDER';
    subscriptionActive: boolean;
    requiresSetup: boolean;
    paymentStatus: string;
    customerEmail?: string;
  }> => {
    const config = token
      ? { headers: { Authorization: `Bearer ${token}` } }
      : undefined;
    const response = await axiosInstance.post(
      `${BASE_URL}/subscriptions/verify-checkout-session`,
      { sessionId },
      config,
    );
    return response.data;
  },

  cancelSubscriptionById: async (
    subscriptionId: string,
  ): Promise<{ success: boolean; message: string; subscription: ShopSubscription }> => {
    const response = await axiosInstance.post(
      `${BASE_URL}/subscriptions/cancel/${subscriptionId}`,
    );
    return response.data;
  },

  upgradeSubscription: async (
    planId: string,
  ): Promise<{ success: boolean; message: string; subscription: ShopSubscription }> => {
    const response = await axiosInstance.post(
      `${BASE_URL}/subscriptions/upgrade`,
      { planId },
    );
    return response.data;
  },
};
